import type { LocalDateDTO } from 'gw-edge-client/dist/types/dto/pc/edge/time/';
import type { CoverableDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';

const isValidDateString = (dateString: string): boolean => {
    const dateRegex = /^\d{2}[-/]\d{2}[-/]\d{4}$/;
    return dateRegex.test(dateString);
};

const convertStringToDate = (dateString: string): Date => {
    if (!isValidDateString(dateString)) {
        throw new Error(`Invalid date format: ${dateString}`);
    }

    const [day, month, year] = dateString.split(/[-/]/).map(Number);
    return new Date(year, month - 1, day);
};

const convertDateToLocalDateDTO = (date: Date): LocalDateDTO => {
    return {
        year: date.getFullYear(),
        month: date.getMonth(),
        day: date.getDate(),
        hours_Top: date.getHours(),
        minutes_Top: date.getMinutes()
    };
};

const convertStringToLocalDateDTO = (dateString: string): LocalDateDTO => {
    const date = convertStringToDate(dateString);
    return convertDateToLocalDateDTO(date);
};

const convertLocalDateDTOToDate = (LocalDateDTO: LocalDateDTO, exactTime: boolean = true): Date => {
    return new Date(
        LocalDateDTO.year ? LocalDateDTO.year : new Date().getFullYear(),
        LocalDateDTO.month ?? new Date().getMonth(),
        LocalDateDTO.day,
        exactTime ? LocalDateDTO.hours_Top ?? 0 : 0,
        exactTime ? LocalDateDTO.minutes_Top ?? 0 : 0
    );
};

const convertLocalDateToString = (date: Date | undefined): string | undefined => {
    return date?.toLocaleDateString('da-DK', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

const convertDateToStringDDMMYYYY = (date: Date, separator: string): string => {
    const day = date.getDate().toString().padStart(2, '0'); // Ensure two digits with leading zero if needed
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Month is zero-based, so add 1
    const year = date.getFullYear().toString();

    return `${day}${separator}${month}${separator}${year}`;
};

const getAsapCancelDate = (isAgriculturalLob: boolean): Date => {
    const futureDate = new Date();
    if (isAgriculturalLob) {
        futureDate.setDate(futureDate.getDate() + 12);
        futureDate.setDate(1);
        futureDate.setMonth(futureDate.getMonth() + 2);
    } else {
        futureDate.setDate(futureDate.getDate() + 30);
        futureDate.setDate(1);
        futureDate.setMonth(futureDate.getMonth() + 1);
    }

    return futureDate;
};

const getNextMonthsFirstDates = (isAgriculturalLob: boolean, isCVR: boolean): Date[] => {
    const currentDate = getAsapCancelDate(isAgriculturalLob);
    const result = [];
    const numberOfDates = isCVR ? 24 : 12;
    for (let i = 0; i < numberOfDates; i++) {
        result.push(new Date(currentDate));
        currentDate.setMonth(currentDate.getMonth() + 1, 1);
    }

    return result;
};

function toLongDateString(localDate: LocalDateDTO | undefined): string {
    if (localDate?.year && localDate.month !== undefined && localDate.day) {
        const date = new Date(Date.UTC(localDate.year, localDate.month, localDate.day, 0, 0, 0));
        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        };
        return date.toLocaleDateString('da-DK', options);
    }
    return '[Invalid date]';
}

function calculateAge(dateOfBirth: LocalDateDTO, daysOffset: number = 0): number {
    const today = new Date().getTime();
    const birthDate = convertLocalDateDTOToDate(dateOfBirth);
    birthDate.setDate(birthDate.getDate() - daysOffset);
    const ageDate = new Date(today - birthDate.getTime());
    return Math.abs(ageDate.getUTCFullYear() - 1970);
}

function calculateAgeDate(date: Date): number {
    const currentDate = new Date();
    if (
        currentDate.getMonth() < date.getMonth() ||
        (currentDate.getMonth() === date.getMonth() && currentDate.getDate() < date.getDate())
    ) {
        return currentDate.getFullYear() - date.getFullYear() - 1;
    }
    return currentDate.getFullYear() - date.getFullYear();
}

function addMonths(date: Date, months: number): Date {
    const newDate = new Date(date);
    const newMonth = newDate.getMonth() + months;
    newDate.setMonth(newMonth);
    return newDate;
}

const coverableEffectiveDate = (coverable: CoverableDTO, ediDate: LocalDateDTO): LocalDateDTO => {
    if ('minorCoverageEarlyDate' in coverable && coverable.minorCoverageEarlyDate) {
        return convertLocalDateDTOToDate(coverable.minorCoverageEarlyDate).getTime() <
            convertLocalDateDTOToDate(ediDate).getTime()
            ? coverable.minorCoverageEarlyDate
            : ediDate;
    }
    return ediDate;
};

const isLocalDateDTO = (obj: object): obj is LocalDateDTO => {
    return 'day' in obj && 'month' in obj && 'year' in obj;
};

const isDate = (obj: object): boolean => {
    return obj instanceof Date || isLocalDateDTO(obj);
};

const convertDateToTrackingString = (date: Date | LocalDateDTO): string => {
    if (date instanceof Date) {
        return convertDateToStringDDMMYYYY(date, '-');
    }
    return convertDateToStringDDMMYYYY(convertLocalDateDTOToDate(date ?? {}), '-');
};

const isDateInThePast = (date: Date): boolean => {
    return date.getTime() < new Date().setHours(0, 0, 0, 0);
};

const areLocalDatesEqual = (
    dateOne: LocalDateDTO | undefined,
    dateTwo: LocalDateDTO | undefined
): boolean => {
    if (dateOne === undefined || dateTwo === undefined) return false;
    return (
        dateOne.year === dateTwo.year &&
        dateOne.month === dateTwo.month &&
        dateOne.day === dateTwo.day
    );
};

const getCoverableChangeDateAsString = (date: LocalDateDTO | undefined): string => {
    const effectiveDate =
        date && !isDateInThePast(convertLocalDateDTOToDate(date))
            ? convertLocalDateDTOToDate(date)
            : new Date();
    return convertLocalDateToString(effectiveDate) ?? '';
};

const isDateDTOInTheFuture = (dateDTO: LocalDateDTO | undefined): boolean => {
    if (dateDTO === undefined) {
        return false;
    }
    const date = convertLocalDateDTOToDate(dateDTO, false);
    return date.getTime() > new Date().setHours(0, 0, 0, 0);
};

export {
    convertDateToLocalDateDTO,
    convertLocalDateDTOToDate,
    convertLocalDateToString,
    getAsapCancelDate,
    getNextMonthsFirstDates,
    toLongDateString,
    convertStringToDate,
    convertStringToLocalDateDTO,
    calculateAge,
    calculateAgeDate,
    addMonths,
    isLocalDateDTO,
    convertDateToStringDDMMYYYY,
    isDate,
    convertDateToTrackingString,
    isDateInThePast,
    areLocalDatesEqual,
    coverableEffectiveDate,
    getCoverableChangeDateAsString,
    isDateDTOInTheFuture
};
